// App.js
import React, { useState } from 'react';
import Home from './Home';
import IntroModuleC from './IntroModuleC';
import IntroModuleE from './IntroModuleE';
import IntroModuleL from './IntroModuleL';
import WelcomeForm from './WelcomeForm';
import GoNoGo from './GoNoGo';
import OneBack from './OneBack';
import Stroop from './Stroop';
import TrailMaking from './TrailMaking';
import PHQ9 from './PHQ9';
import SampleReport from './SampleReport';
import Glossary from './Glossary';

function App() {
  const [step, setStep] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [results, setResults] = useState({});

  const mockUser = {
    firstName: 'John',
    lastName: 'Doe',
    email: 'your.client@cogello.com',
    age: '32',
    gender: 'Male'
  };

  const mockResults = {
    goNoGo: { correct: 48, totalTrials: 50, avgRT: 450 },
    oneBack: { correct: 32, misses: 6, falseAlarms: 2, avgRT: 700 },
    stroop: { correct: 24, avgRT: 650 },
    trailMaking: [
      { part: 'A', duration: 28000, errors: 0 },
      { part: 'B', duration: 70000, errors: 2 }
    ],
    phq9: { total: 3, severity: 'Minimal' }
  };

  const handleNext = (key, data) => {
    if (key) {
      setResults(prev => ({ ...prev, [key]: data }));
    }
    setStep(prev => prev + 1);
  };

  const finalUserInfo = previewMode ? mockUser : userInfo;
  const finalResults = previewMode ? mockResults : results;

  const getThresholdLabel = (task, value) => {
    switch (task) {
      case 'goNoGo':
        const goAcc = (value.correct / value.totalTrials) * 100;
        return goAcc > 85 ? 'Within Expected' : goAcc >= 70 ? 'Borderline' : 'Below Expected';
      case 'oneBack':
        const oneAcc = (value.correct / 40) * 100;
        const penalty = value.misses * 1.5 + value.falseAlarms * 2;
        return oneAcc - penalty > 75 ? 'Within Expected' : oneAcc - penalty >= 60 ? 'Borderline' : 'Below Expected';
      case 'stroop':
        const stroopAcc = (value.correct / value.totalTrials) * 100;
        return stroopAcc > 85 ? 'Within Expected' : stroopAcc >= 70 ? 'Borderline' : 'Below Expected';
      case 'trailMaking':
        const durB = value[1].duration;
        const errB = value[1].errors;
        return (errB === 0 && durB < 45000) ? 'Within Expected' : (errB <= 2 && durB <= 60000) ? 'Borderline' : 'Below Expected';
      case 'phq9':
        return value.total < 5 ? 'Within Expected' : value.total <= 9 ? 'Borderline' : 'Below Expected';
      default:
        return '';
    }
  };

  const getInterpretation = (task, label) => {
    const map = {
      goNoGo: {
        'Within Expected': 'Strong inhibitory control and attentional consistency.',
        'Borderline': 'Some difficulty suppressing impulsive responses or maintaining attention.',
        'Below Expected': 'Impaired response inhibition and attentional lapses evident.'
      },
      oneBack: {
        'Within Expected': 'Working memory appears intact.',
        'Borderline': 'Mild working memory lapses or inconsistent attention.',
        'Below Expected': 'Significant difficulties with working memory and sustained attention.'
      },
      stroop: {
        'Within Expected': 'Effective cognitive control and interference resolution.',
        'Borderline': 'Possible difficulty resolving conflicting information.',
        'Below Expected': 'Likely impairment in interference control or cognitive flexibility.'
      },
      trailMaking: {
        'Within Expected': 'Good processing speed and cognitive flexibility.',
        'Borderline': 'Mild slowing or set-shifting difficulty.',
        'Below Expected': 'Significant difficulty with processing speed and cognitive flexibility.'
      },
      phq9: {
        'Within Expected': 'No significant mood symptoms reported.',
        'Borderline': 'Some symptoms of depression may affect functioning.',
        'Below Expected': 'Elevated mood symptoms; clinical attention recommended.'
      }
    };
    return map[task]?.[label] || '';
  };

  const handleSelectContext = (context) => {
    if (context === 'clinical') setStep(1);
    else if (context === 'education') setStep(2);
    else if (context === 'legal') setStep(3);
  };

  const steps = [
    () => <Home setStep={setStep} onSelectContext={handleSelectContext} />,
    () => <IntroModuleC onContinue={() => setStep(4)} onHome={() => setStep(0)} onGlossary={() => setStep(11)} />,
    () => <IntroModuleE onContinue={() => setStep(4)} onHome={() => setStep(0)} onGlossary={() => setStep(11)} />,
    () => <IntroModuleL onContinue={() => setStep(4)} onHome={() => setStep(0)} onGlossary={() => setStep(11)} />,
    () => <WelcomeForm 
            onSubmit={(data) => { setUserInfo(data); setStep(5); }}
            onPreview={() => { setPreviewMode(true); setStep(10); }} 
            onHome={() => setStep(0)} 
            onGlossary={() => setStep(11)} 
          />,
    () => <GoNoGo onComplete={(data) => handleNext('goNoGo', data)} />,
    () => <OneBack onComplete={(data) => handleNext('oneBack', data)} />,
    () => <Stroop onComplete={(data) => handleNext('stroop', data)} />,
    () => <TrailMaking onComplete={(data) => handleNext('trailMaking', data)} />,
    () => (
      <PHQ9
        onComplete={(data) => {
        setPreviewMode(false);
        handleNext('phq9', data);
        setStep(12);
     }}
    />
    ),
    () => <SampleReport onHome={() => setStep(0)} onRunFullBattery={() => { setPreviewMode(false); setStep(4); }} onGlossary={() => setStep(11)} />,
    () => <Glossary onHome={() => setStep(0)} onRunFullBattery={() => { setPreviewMode(false); setStep(4); }} />,
    () => {
      const {
        goNoGo = {},
        oneBack = {},
        stroop = {},
        trailMaking = [],
        phq9 = {}
      } = finalResults;

      return (
        <div style={{ textAlign: 'center', marginTop: '3rem', maxWidth: '700px', margin: 'auto', paddingBottom: '3rem' }}>
          <div style={{ position: 'absolute', top: '1rem', left: '1rem', textAlign: 'left' }}>
            <button onClick={() => setStep(0)} style={buttonStyle}>🏠 Home</button>
            <button onClick={() => setStep(11)} style={buttonStyle}>📘 Glossary</button>
          </div>
          <h2 style={{ fontSize: '2rem', marginTop: '5rem' }}>
            <span style={{ color: '#149854' }}>Cogello</span> Cognitive Report
          </h2>
          <hr style={{ margin: '1.5rem 0' }} />

          <h3>Go/No-Go (Inhibition & Attention)</h3>
          <p>{goNoGo.correct} correct out of {goNoGo.totalTrials}. Avg RT: {Math.round(goNoGo.avgRT)} ms</p>
          <strong>{getThresholdLabel('goNoGo', goNoGo)}</strong>
          <p><em>{getInterpretation('goNoGo', getThresholdLabel('goNoGo', goNoGo))}</em></p>

          <h3>1-Back (Working Memory)</h3>
          <p>{oneBack.correct} correct, {oneBack.misses} misses, {oneBack.falseAlarms} false alarms. Avg RT: {Math.round(oneBack.avgRT)} ms</p>
          <strong>{getThresholdLabel('oneBack', oneBack)}</strong>
          <p><em>{getInterpretation('oneBack', getThresholdLabel('oneBack', oneBack))}</em></p>

          <h3>Stroop (Cognitive Control)</h3>
          <p>{stroop.correct} correct. Avg RT: {Math.round(stroop.avgRT)} ms</p>
          <strong>{getThresholdLabel('stroop', stroop)}</strong>
          <p><em>{getInterpretation('stroop', getThresholdLabel('stroop', stroop))}</em></p>

          <h3>Trail Making (Processing Speed & Set-Shifting)</h3>
          {trailMaking.map(part => (
            <p key={part.part}>Part {part.part}: {Math.round(part.duration / 1000)}s, {part.errors} error{part.errors !== 1 ? 's' : ''}</p>
          ))}
          <strong>{getThresholdLabel('trailMaking', trailMaking)}</strong>
          <p><em>{getInterpretation('trailMaking', getThresholdLabel('trailMaking', trailMaking))}</em></p>

          <h3>PHQ-9 (Mood Screener)</h3>
          <p>Total Score: {phq9.total}, Severity: {phq9.severity}</p>
          <strong>{getThresholdLabel('phq9', phq9)}</strong>
          <p><em>{getInterpretation('phq9', getThresholdLabel('phq9', phq9))}</em></p>

          <hr />
          <p style={{ fontStyle: 'italic', marginTop: '1.5rem' }}>
            This brief cognitive report is for screening purposes only. It is not a formal diagnosis.
          </p>
        </div>
      );
    }
  ];

  const buttonStyle = {
    padding: '0.6rem 1rem',
    marginRight: '0.5rem',
    backgroundColor: '#eee',
    border: '1px solid #ccc',
    borderRadius: '6px',
    cursor: 'pointer',
    width: '200px'
  };

  return <div className="App">{steps[step]()}</div>;
}

export default App;
